import {
	dispatchAcknowledgedEvent,
	openResourceSection,
} from '../../core/resourceEvents';
import {highlightExistingAction} from '../../core/actionHighlighter';
import {
	ALARMS_SECTION_EVENT,
	ACTIVE_ALARMS_FILTER_EVENT,
	ACTIVE_ALARMS_FOCUS_EVENT,
	ACTIVE_ALARMS_SECTION_EVENT,
	HISTORICAL_ALARMS_FILTER_EVENT,
	HISTORICAL_ALARMS_FOCUS_EVENT,
	HISTORICAL_ALARMS_SECTION_EVENT,
	SWITCH_EVENTS_FILTER_EVENT,
	SWITCH_EVENTS_FOCUS_EVENT,
	SWITCH_EVENTS_SECTION_EVENT,
} from './events';

const ALARMS_SECTIONS = new Set(['maintenance.alarms']);
const ACTIVE_SECTIONS = new Set(['maintenance.active_alarms']);
const HISTORICAL_SECTIONS = new Set(['maintenance.historical_alarms']);
const SWITCH_SECTIONS = new Set(['maintenance.switch_events']);

/**
 * Open a Maintenance alarm accordion and optionally highlight a toolbar action.
 * @param {string} eventName section event
 * @param {string} sectionId semantic section
 * @param {Set<string>} allowedSections section allowlist
 * @param {object} command experience command
 * @param {string} refreshError error when refresh is missing
 * @param {string} bulkError error when bulk select is missing
 * @return {Promise<void>} completion
 */
async function openAlarmInventory(
	eventName,
	sectionId,
	allowedSections,
	command,
	refreshError,
	bulkError,
) {
	const seen = await openResourceSection(eventName, sectionId, allowedSections);
	const target = command.params?.action_target;
	const activation = {
		activate: command.params?.action_activation === 'auto',
		commandId: command.command_id,
	};
	if (target === 'refresh' &&
		!highlightExistingAction(['Refresh'], [], activation)) {
		throw new Error(refreshError);
	}
	if (target === 'bulk_select_delete' &&
		!highlightExistingAction(['Delete'], [], activation)) {
		throw new Error(bulkError);
	}
	// Carried back rather than awaited and dropped. The section says
	// what the page actually opened; a wrapper returning nothing
	// turns that into an empty acknowledgement, and the backend then
	// cannot tell a section that opened from one that did not.
	return seen;
}

/**
 * Open the Alarms tab without selecting an accordion.
 * @return {Promise<void>} completion
 */
export function openAlarmsSection() {
	return openResourceSection(
		ALARMS_SECTION_EVENT,
		'maintenance.alarms',
		ALARMS_SECTIONS,
	);
}

/**
 * Open Active Alarms.
 * @param {object} command experience command
 * @return {Promise<void>} completion
 */
export function openActiveAlarmsSection(command = {}) {
	return openAlarmInventory(
		ACTIVE_ALARMS_SECTION_EVENT,
		'maintenance.active_alarms',
		ACTIVE_SECTIONS,
		command,
		'alarms_refresh_unavailable',
		'alarms_bulk_selection_unavailable',
	);
}

/**
 * Open Historical Alarms.
 * @param {object} command experience command
 * @return {Promise<void>} completion
 */
export function openHistoricalAlarmsSection(command = {}) {
	return openAlarmInventory(
		HISTORICAL_ALARMS_SECTION_EVENT,
		'maintenance.historical_alarms',
		HISTORICAL_SECTIONS,
		command,
		'historical_alarms_refresh_unavailable',
		'historical_alarms_bulk_selection_unavailable',
	);
}

/**
 * Open Switch Events.
 * @param {object} command experience command
 * @return {Promise<void>} completion
 */
export function openSwitchEventsSection(command = {}) {
	return openAlarmInventory(
		SWITCH_EVENTS_SECTION_EVENT,
		'maintenance.switch_events',
		SWITCH_SECTIONS,
		command,
		'switch_events_refresh_unavailable',
		'switch_events_bulk_selection_unavailable',
	);
}

/**
 * Dispatch a list filter event.
 * @param {string} eventName filter event
 * @param {string} resource entity type
 * @param {object} command filter command
 * @return {Promise<void>} completion acknowledgement
 */
function applyAlarmFilter(eventName, resource, command) {
	const params = command.params || {};
	const query = String(params.query || '').trim();
	const filters = {};
	Object.entries(params).forEach(([key, value]) => {
		if (key === 'query' || key === 'resource') return;
		const text = String(value ?? '').trim();
		if (text) filters[key] = text;
	});
	if (!query && !Object.keys(filters).length) {
		return Promise.reject(new Error(`invalid_${resource}_filter`));
	}
	return dispatchAcknowledgedEvent(eventName, {
		query,
		...filters,
		resource,
	});
}

/**
 * Apply an Active Alarms list query or column filter.
 * @param {object} command filter command
 * @return {Promise<void>} completion acknowledgement
 */
export function applyActiveAlarmsFilter(command) {
	return applyAlarmFilter(ACTIVE_ALARMS_FILTER_EVENT, 'alarm', command);
}

/**
 * Apply a Historical Alarms list query or column filter.
 * @param {object} command filter command
 * @return {Promise<void>} completion acknowledgement
 */
export function applyHistoricalAlarmsFilter(command) {
	return applyAlarmFilter(
		HISTORICAL_ALARMS_FILTER_EVENT,
		'historical_alarm',
		command,
	);
}

/**
 * Apply a Switch Events list query or column filter.
 * @param {object} command filter command
 * @return {Promise<void>} completion acknowledgement
 */
export function applySwitchEventsFilter(command) {
	return applyAlarmFilter(SWITCH_EVENTS_FILTER_EVENT, 'switch_event', command);
}

/**
 * Focus a resolved alarm row.
 * @param {string} eventName focus event
 * @param {string} resource entity type
 * @param {object} command focus command
 * @return {Promise<void>} completion acknowledgement
 */
function focusAlarm(eventName, resource, command) {
	const entityId = command.params?.entity_id;
	if (typeof entityId !== 'string' || !entityId) {
		return Promise.reject(new Error(`invalid_${resource}_entity`));
	}
	return dispatchAcknowledgedEvent(eventName, {
		entityId,
		action_target: command.params?.action_target,
		action_activation: command.params?.action_activation,
		resource,
	});
}

/**
 * Focus a resolved Active Alarm row.
 * @param {object} command focus command
 * @return {Promise<void>} completion acknowledgement
 */
export function focusActiveAlarm(command) {
	return focusAlarm(ACTIVE_ALARMS_FOCUS_EVENT, 'alarm', command);
}

/**
 * Focus a resolved Historical Alarm row.
 * @param {object} command focus command
 * @return {Promise<void>} completion acknowledgement
 */
export function focusHistoricalAlarm(command) {
	return focusAlarm(
		HISTORICAL_ALARMS_FOCUS_EVENT,
		'historical_alarm',
		command,
	);
}

/**
 * Focus a resolved Switch Event row.
 * @param {object} command focus command
 * @return {Promise<void>} completion acknowledgement
 */
export function focusSwitchEvent(command) {
	return focusAlarm(SWITCH_EVENTS_FOCUS_EVENT, 'switch_event', command);
}
